import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { RefreshCw, Search, X, AlertTriangle, CheckCircle, FileText, User, Calendar, Package, ArrowUp, Truck, Info } from 'lucide-react';
import MainService from '../../services/MainService';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPageHeading } from '../../locales/navigation';

const PushOrder = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Selected delivery note
  const [selectedDeliveryNote, setSelectedDeliveryNote] = useState(null);
  const [deliveryNoteDetails, setDeliveryNoteDetails] = useState([]);

  // Available orders (for pushing)
  const [availableOrders, setAvailableOrders] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [pageSize] = useState(20);
  const [searchKeyword, setSearchKeyword] = useState('');

  // Modal states
  const [showDeliveryNoteModal, setShowDeliveryNoteModal] = useState(false);
  const [deliveryNotes, setDeliveryNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [modalSearchKeyword, setModalSearchKeyword] = useState('');

  // ==================== Effects ====================
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // ==================== Helper Functions ====================
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '-';
    return `AED ${parseFloat(amount).toFixed(2)}`;
  };

  const getAddressString = (address, type = 'client') => {
    if (!address || !address.Location) return '-';
    const parts = [
      address.Location.Name,
      address.Location.Area?.Name,
      address.Emirate?.Name
    ].filter(Boolean);
    return parts.join(', ') || '-';
  };

  // ==================== API Functions ====================
  const loadDeliveryNotes = async () => {
    try {
      setLoadingNotes(true);
      const response = await MainService.GetConfirmedDeliveryNotes();
      if (response && Array.isArray(response)) {
        setDeliveryNotes(response);
      } else if (response && response.Data) {
        setDeliveryNotes(response.Data);
      }
    } catch (err) {
      console.error('Error loading delivery notes:', err);
      setError('Failed to load delivery notes');
    } finally {
      setLoadingNotes(false);
    }
  };

  const loadAvailableOrders = async (skip = 0, append = false, totalCount = null, deliveryNoteId = null) => {
    try {
      if (skip === 0) {
        setLoading(true);
      } else {
        setIsLoadingMore(true);
      }
      
      const requestData = {
        Skip: skip,
        Take: pageSize,
        DeliveryNoteId: deliveryNoteId || selectedDeliveryNote?.Id || null
      };

      const response = await MainService.OrdersInDeliveryNote(requestData);
      
      if (response) {
        const total = totalCount !== null ? totalCount : (response.Total || (Array.isArray(response) ? response.length : (response.Data ? response.Data.length : 0)));
        const newData = Array.isArray(response) ? response : (response.Data || []);
        
        if (skip === 0) {
          setTotalOrders(total);
          setAvailableOrders(newData);
          
          const loadedCount = newData.length;
          if (loadedCount > 0 && loadedCount === pageSize && skip + pageSize < total) {
            setTimeout(() => {
              loadAvailableOrders(pageSize, true, total, deliveryNoteId);
            }, 100);
          }
        } else {
          setAvailableOrders(prev => {
            const existingIds = new Set(prev.map(order => order.Id));
            const uniqueNewData = newData.filter(order => !existingIds.has(order.Id));
            const updated = [...prev, ...uniqueNewData];
            const loadedCount = updated.length;
            
            const nextSkip = skip + pageSize;
            if (loadedCount < total && newData.length === pageSize && uniqueNewData.length > 0 && nextSkip < total) {
              setTimeout(() => {
                loadAvailableOrders(nextSkip, true, total, deliveryNoteId);
              }, 100);
            } else if (loadedCount >= total) {
              if (loadedCount > total) {
                setTotalOrders(loadedCount);
              }
            }
            
            return updated;
          });
        }
      } else {
        if (skip === 0) {
          setAvailableOrders([]);
          setTotalOrders(0);
        }
      }
    } catch (err) {
      console.error('Error loading available orders:', err);
      if (skip === 0) {
        setError('Failed to load orders');
        setAvailableOrders([]);
        setTotalOrders(0);
      }
    } finally {
      if (skip === 0) {
        setLoading(false);
      } else {
        setIsLoadingMore(false);
      }
    }
  };

  const loadDeliveryNoteDetails = async (noteId) => {
    try {
      const response = await MainService.GetDeliveryDetailsListById(noteId);
      if (response && Array.isArray(response)) {
        setDeliveryNoteDetails(response);
      } else if (response && response.Data) {
        setDeliveryNoteDetails(response.Data);
      } else {
        setDeliveryNoteDetails([]);
      }
    } catch (err) {
      console.error('Error loading delivery note details:', err);
      setDeliveryNoteDetails([]);
    }
  };

  // ==================== Event Handlers ====================
  const openDeliveryNoteModal = () => {
    setModalSearchKeyword('');
    loadDeliveryNotes();
    setShowDeliveryNoteModal(true);
  };

  const selectDeliveryNote = async (note) => {
    try {
      setLoading(true);
      setSelectedDeliveryNote(note);
      await loadDeliveryNoteDetails(note.Id);
      await loadAvailableOrders(0, false, null, note.Id);
      setShowDeliveryNoteModal(false);
    } catch (err) {
      console.error('Error selecting delivery note:', err);
      setError('Network Error');
    } finally {
      setLoading(false);
    }
  };

  const pushOrder = async (order) => {
    if (!selectedDeliveryNote?.Id) {
      setError('Select Delivery Note');
      return;
    }

    try {
      setLoading(true);
      const response = await MainService.PushOrder({
        OrderId: order.Id,
        DeliveryNoteId: selectedDeliveryNote.Id
      });
      
      if (response === 'Success' || (response && response.Status === 'Success')) {
        setSuccess('Order Pushed Successfully');
        await loadDeliveryNoteDetails(selectedDeliveryNote.Id);
        await loadAvailableOrders(0, false, null, selectedDeliveryNote.Id);
      } else {
        setError(response || response.Status || 'Something went wrong');
      }
    } catch (err) {
      console.error('Error pushing order:', err);
      setError('Network Error');
    } finally {
      setLoading(false);
    }
  };

  // ==================== Filter Functions ====================
  const filteredDeliveryNotes = deliveryNotes.filter(note => {
    if (!modalSearchKeyword) return true;
    const lowerKeyword = modalSearchKeyword.toLowerCase();
    return (note.RefNo || '').toLowerCase().includes(lowerKeyword) ||
      ((note.Driver || {}).Name || '').toLowerCase().includes(lowerKeyword) ||
      (note.Status || '').toLowerCase().includes(lowerKeyword);
  });

  const filteredAvailableOrders = availableOrders.filter(order => {
    if (!searchKeyword) return true;
    const lowerKeyword = searchKeyword.toLowerCase();
    return String(order.VoucherNo ?? '').toLowerCase().includes(lowerKeyword) ||
      ((order.Client || {}).Name || '').toLowerCase().includes(lowerKeyword) ||
      ((order.Customer || {}).Name || '').toLowerCase().includes(lowerKeyword) ||
      String(order.ClientBillNo ?? '').toLowerCase().includes(lowerKeyword);
  });

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ArrowUp className="w-6 h-6 text-primary" />
              {getPageHeading(language, 'pushOrder')}
            </h1>
            <p className="text-sm text-gray-500 mt-1">{getPageHeading(language, 'pushOrderSub')}</p>
          </div>
        </div>
      </div>

      {/* Toast Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            <span>{error}</span>
          </div>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            <span>{success}</span>
          </div>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Delivery Note Selection Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {!selectedDeliveryNote ? (
          <div className="text-center py-8">
            <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Delivery Note Selected</h3>
            <p className="text-sm text-gray-500 mb-4">Select a delivery note to start pushing orders</p>
            <button
              onClick={openDeliveryNoteModal}
              className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors flex items-center gap-2 mx-auto"
            >
              <FileText className="w-5 h-5" />
              Select Delivery Note
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <FileText className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Selected Delivery Note</h3>
                  <p className="text-sm text-gray-500">Ready to push orders</p>
                </div>
              </div>
              <button
                onClick={openDeliveryNoteModal}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2 text-sm"
              >
                <RefreshCw className="w-4 h-4" />
                Change
              </button>
            </div>
            
            {/* Delivery Note Info Card */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-5 border border-blue-100">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FileText className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase block">Ref No</span>
                    <span className="text-sm font-semibold text-gray-900">{selectedDeliveryNote.RefNo || '-'}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Calendar className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase block">Date</span>
                    <span className="text-sm font-semibold text-gray-900">{formatDate(selectedDeliveryNote.Date)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <User className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase block">Driver</span>
                    <span className="text-sm font-semibold text-gray-900">{(selectedDeliveryNote.Driver || {}).Name || '-'}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Package className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase block">Orders</span>
                    <span className="text-sm font-semibold text-gray-900">{deliveryNoteDetails.length}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Orders in Delivery Note */}
      {selectedDeliveryNote && deliveryNoteDetails.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 bg-white border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-600" />
                Orders in Delivery Note ({deliveryNoteDetails.length})
              </h2>
            </div>
          </div>
          <div className="overflow-x-auto" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Voucher No</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client Bill No</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client Address</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer Address</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {deliveryNoteDetails.map((detail, index) => (
                  <tr key={detail.Id || index} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-gray-600">{index + 1}</td>
                    <td className="px-4 py-3 text-gray-900 font-medium">{(detail.Order || {}).VoucherNo || '-'}</td>
                    <td className="px-4 py-3 text-gray-600">{(detail.Order || {}).ClientBillNo || '-'}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {((detail.Order || {}).Client || {}).Name || '-'}
                      {((detail.Order || {}).Client || {}).MobileNo1 && (
                        <span className="text-gray-400 ml-1">({((detail.Order || {}).Client || {}).MobileNo1})</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600 max-w-[200px] truncate" title={getAddressString((detail.Order || {}).ClientAddress, 'client')}>
                      {getAddressString((detail.Order || {}).ClientAddress, 'client')}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {((detail.Order || {}).Customer || {}).Name || '-'}
                      {((detail.Order || {}).Customer || {}).MobileNo1 && (
                        <span className="text-gray-400 ml-1">({((detail.Order || {}).Customer || {}).MobileNo1})</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600 max-w-[200px] truncate" title={getAddressString((detail.Order || {}).CustomerAddress, 'customer')}>
                      {getAddressString((detail.Order || {}).CustomerAddress, 'customer')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Available Orders Table */}
      {selectedDeliveryNote && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 bg-white border-b border-gray-200">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Package className="w-5 h-5 text-green-600" />
                  Available Orders
                </h2>
                {availableOrders.length > 0 && (
                  <p className="text-sm text-gray-600 mt-1">
                    Showing {availableOrders.length} of {totalOrders} orders
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search by voucher, client, customer..."
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm w-64"
                  />
                </div>
                <button
                  onClick={() => loadAvailableOrders(0, false, null, selectedDeliveryNote.Id)}
                  disabled={loading}
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors flex items-center gap-2 text-sm font-medium"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto" style={{ maxHeight: '600px', overflowY: 'auto' }}>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">S.No</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Voucher No</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Client Bill</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Delivery Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Product Price</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Delivery Charge</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">TAX</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Customer Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Client Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Client</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Client Mob</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Client Emirate</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Client Location</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Customer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Customer Mob</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Customer Emirate</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Customer Location</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap min-w-[150px]">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading && filteredAvailableOrders.length === 0 ? (
                  <tr>
                    <td colSpan={19} className="px-4 py-12 text-center">
                      <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-primary" />
                      <p className="text-gray-500">Loading orders...</p>
                    </td>
                  </tr>
                ) : filteredAvailableOrders.length === 0 ? (
                  <tr>
                    <td colSpan={19} className="px-4 py-12 text-center text-gray-500">
                      <Package className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                      <p className="text-lg mb-2 font-medium">No orders found</p>
                      <p className="text-sm">Try adjusting your search or refresh the page</p>
                    </td>
                  </tr>
                ) : (
                  filteredAvailableOrders.map((order, index) => (
                    <tr key={order.Id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-gray-600">{index + 1}</td>
                      <td className="px-4 py-3 text-gray-900 font-medium">{order.VoucherNo || '-'}</td>
                      <td className="px-4 py-3 text-gray-600">{order.ClientBillNo || '-'}</td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDate(order.DeliveryDate)}</td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatCurrency(order.ProductPrice)}</td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatCurrency(order.DeliveryCharge)}</td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatCurrency(order.TAX)}</td>
                      <td className="px-4 py-3 text-gray-600 font-medium whitespace-nowrap">{formatCurrency(order.CustomerAmount)}</td>
                      <td className="px-4 py-3 text-gray-600 font-medium whitespace-nowrap">{formatCurrency(order.ClientAmount)}</td>
                      <td className="px-4 py-3 text-gray-600">{((order.Client || {}).Name || '-')}</td>
                      <td className="px-4 py-3 text-gray-600">{((order.Client || {}).MobileNo1 || '-')}</td>
                      <td className="px-4 py-3 text-gray-600">{((order.ClientAddress || {}).Emirate || {}).Name || '-'}</td>
                      <td className="px-4 py-3 text-gray-600">{((order.ClientAddress || {}).Location || {}).Name || '-'}</td>
                      <td className="px-4 py-3 text-gray-600">{((order.Customer || {}).Name || '-')}</td>
                      <td className="px-4 py-3 text-gray-600">{((order.Customer || {}).MobileNo1 || '-')}</td>
                      <td className="px-4 py-3 text-gray-600">{((order.CustomerAddress || {}).Emirate || {}).Name || '-'}</td>
                      <td className="px-4 py-3 text-gray-600">{((order.CustomerAddress || {}).Location || {}).Name || '-'}</td>
                      <td className="px-4 py-3 min-w-[150px]">
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          {((order.Status || {}).Name || '-')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => pushOrder(order)}
                          disabled={loading}
                          className="px-3 py-1.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all flex items-center gap-1.5 text-xs font-medium shadow-sm hover:shadow"
                        >
                          <ArrowUp className="w-3.5 h-3.5" />
                          Push
                        </button>
                      </td>
                    </tr>
                  ))
                )}
                {isLoadingMore && (
                  <tr>
                    <td colSpan={19} className="px-4 py-4 text-center bg-gray-50">
                      <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-1 text-primary" />
                      <p className="text-xs text-gray-500">Loading more orders...</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {loading && ReactDOM.createPortal(
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg p-6 flex items-center gap-3 shadow-xl">
            <RefreshCw className="w-6 h-6 animate-spin text-primary" />
            <span className="text-gray-700 font-medium">Loading...</span>
          </div>
        </div>,
        document.body
      )}

      {/* Select Delivery Note Modal */}
      {showDeliveryNoteModal && ReactDOM.createPortal(
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Select Delivery Note</h2>
                <button
                  onClick={() => setShowDeliveryNoteModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="mt-4 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by Ref No, Driver, or Status..."
                  value={modalSearchKeyword}
                  onChange={(e) => setModalSearchKeyword(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {loadingNotes ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : filteredDeliveryNotes.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  <p>No delivery notes found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref No</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Remarks</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehicle</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created By</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Updated By</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Options</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredDeliveryNotes.map((note, index) => (
                        <tr key={note.Id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => selectDeliveryNote(note)}>
                          <td className="px-4 py-3 text-gray-600">{index + 1}</td>
                          <td className="px-4 py-3 text-gray-600">{formatDate(note.Date)}</td>
                          <td className="px-4 py-3 text-gray-900 font-medium">{note.RefNo || '-'}</td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                              {note.Status || '-'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-600">{note.Remarks || '-'}</td>
                          <td className="px-4 py-3 text-gray-600">{(note.Driver || {}).Name || '-'}</td>
                          <td className="px-4 py-3 text-gray-600">
                            {note.Vehicle ? `${note.Vehicle.Number || ''}${note.Vehicle.Model ? `, ${note.Vehicle.Model}` : ''}` : '-'}
                          </td>
                          <td className="px-4 py-3 text-gray-600">{note.CreatedName || '-'}</td>
                          <td className="px-4 py-3 text-gray-600">{note.UpdatedName || '-'}</td>
                          <td className="px-4 py-3">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                selectDeliveryNote(note);
                              }}
                              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
                            >
                              Select
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default PushOrder;
